import React, { useState } from "react";
import { TextInput } from "@sprout/text_input";
import useTranslation from "@hooks/use_translation";
import { PrimaryButton } from "@sprout/buttons";
import { router } from "@inertiajs/react";
import SocialLoginButtons, { type SocialLogin } from "@components/social_login_buttons";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";
import useDevice from "@hooks/use_device";
import { authTrackingPayload, type AuthTracking } from "@/utils/auth_tracking";

const EMAIL_FORM_ID = "login-email-form";
const EMAIL_CODE = "email_code";

type EmailFormProps = {
  jobBoardToken: string;
  createPath: string;
  email: string;
  socialLogin: SocialLogin;
  setShowCode: (showCode: boolean) => void;
  setEmail: (email: string) => void;
  authTracking?: AuthTracking | null;
};

const LoginEmail = (props: EmailFormProps) => {
  const { jobBoardToken, createPath, email, socialLogin, setShowCode, setEmail, authTracking } =
    props;
  const { t } = useTranslation("misc");
  const { trackEvent } = useSnowplowTracker();
  const { deviceType } = useDevice();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultErrorMessage = t("defaultError");
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    trackEvent(SnowplowEvent.AuthMethodSelected, {
      ...authTrackingPayload(authTracking),
      auth_method: EMAIL_CODE,
    });

    if (!emailRegex.test(email)) {
      setError(t("login.invalidEmail"));
      return;
    }

    setIsSubmitting(true);

    router.post(
      createPath,
      {
        email: email,
        job_board: jobBoardToken,
        device_type: deviceType,
      },
      {
        onError: (errors) => {
          setError(errors["email"] || errors["_server"] || defaultErrorMessage);
        },
        onFinish: () => setIsSubmitting(false),
        onSuccess: () => setShowCode(true),
      },
    );
  };

  return (
    <div className="login__auth">
      <form id={EMAIL_FORM_ID} className="login__form" onSubmit={submit}>
        <TextInput
          id="email-address"
          placeholder={t("login.email")}
          type="email"
          autoComplete="email"
          required
          onChange={(event) => setEmail(event.currentTarget.value || "")}
          maxLength={255}
          value={email}
          error={error}
          autoFocus
        />
      </form>

      <div className="login__actions">
        <PrimaryButton form={EMAIL_FORM_ID} loading={isSubmitting} type="submit">
          {t("login.sendCode")}
        </PrimaryButton>

        <SocialLoginButtons
          socialLogin={socialLogin}
          jobBoardToken={jobBoardToken}
          authTracking={authTracking}
        />
      </div>
    </div>
  );
};

export default LoginEmail;
